import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { QrCode, ImagePlus, Loader2, X, Save } from "lucide-react";

export default function PixSettingsDialog({ open, onOpenChange }) {
  const { user, checkAuth } = useAuth();
  const [pixKey, setPixKey] = useState("");
  const [pixQr, setPixQr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPixKey(user?.pix_key || "");
      setPixQr(user?.pix_qr || "");
    }
  }, [open, user]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPixQr(data.path);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/me/pix", { pix_key: pixKey || null, pix_qr: pixQr || null });
      await checkAuth();
      toast.success("Dados do Pix salvos!");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pix-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-[#1e7be0]" /> Meu Pix</DialogTitle>
          <DialogDescription>
            Cadastre sua chave Pix e o QR Code (imagem gerada no app do seu banco). Serão exibidos ao cliente na Ordem de Serviço com pagamento via Pix.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Chave Pix</Label>
            <Input value={pixKey} onChange={(e) => setPixKey(e.target.value)}
              placeholder="CPF/CNPJ, e-mail, telefone ou aleatória" data-testid="pix-key-input" />
          </div>
          <div className="space-y-1.5">
            <Label>QR Code do Pix (imagem)</Label>
            {pixQr ? (
              <div className="relative inline-block">
                <img src={fileUrl(pixQr)} alt="QR Pix" className="h-40 w-40 object-contain rounded-md border border-slate-200 bg-white" data-testid="pix-qr-preview" />
                <button type="button" onClick={() => setPixQr("")}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid="pix-qr-remove">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="h-40 w-40 rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 text-slate-400 gap-1"
                data-testid="pix-qr-upload-label">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                <span className="text-xs">Enviar QR Code</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} data-testid="pix-qr-input" />
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={save} disabled={busy} data-testid="pix-save-button">
            <Save className="h-4 w-4" /> {busy ? "Salvando..." : "Salvar Pix"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
